import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3500';
import { doStartProjectScan, luHandleAppiError, type U3500ConsigneDto } from '../U3500Service';

export interface UseU3500ProjectScanHandlersParams {
  onSuccess?: (consigne: U3500ConsigneDto) => void;
  onError?: (error: unknown) => void;
}

/**
 * Handler : enqueue SCAN inventory du dossier NAS d’un projet — POST U3500/projects/:id/scan.
 */
export function useU3500ProjectScanHandlers(params: UseU3500ProjectScanHandlersParams = {}) {
  const { onSuccess, onError } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const startProjectScan = useCallback(
    async (projectId: string): Promise<U3500ConsigneDto | null> => {
      if (!projectId?.trim()) {
        return null;
      }
      try {
        const consigne = await doStartProjectScan(axios, projectId.trim());
        onSuccess?.(consigne);
        return consigne;
      } catch (error: unknown) {
        const err = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError
        );
        if (err.genericErrors?.length) {
          err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        }
        onError?.(error);
        return null;
      }
    },
    [axios, onSuccess, onError, t, toast]
  );

  return { startProjectScan };
}
